import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';

/**
 * Cell content for accordion-row tables: a chevron toggle followed by the
 * cell's content. Rows that can't expand render an equal-width spacer so text
 * stays column-aligned with expandable rows.
 */
type GridCellExpandProps = React.ComponentProps<'div'> & {
  /** Whether this row has children to expand. When false, renders the alignment spacer. */
  canExpand?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  /** Test id for the chevron toggle button (the interactive part). */
  toggleTestId?: string;
};

function GridCellExpand({
  className,
  canExpand = false,
  isExpanded = false,
  onToggle,
  toggleTestId,
  children,
  ...props
}: GridCellExpandProps) {
  return (
    <div data-slot="grid-cell-expand" className={cn('flex min-w-0 items-center gap-0.5', className)} {...props}>
      {canExpand ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
          data-testid={toggleTestId}
          className="text-grey-90 hover:bg-grey-20 flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded-xs focus-ring"
        >
          <Icon icon={isExpanded ? 'chevronUp' : 'chevronRight'} size={14} />
        </button>
      ) : (
        <span aria-hidden="true" className="w-4 shrink-0" />
      )}
      {children}
    </div>
  );
}

export { GridCellExpand, type GridCellExpandProps };
